import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { supabase } from '../../../supabase';
import '../../styles/AdminLogin.css';

// Same floor as my-app's account passwords - at least 8 characters and one number.
const isPasswordStrongEnough = (password) => Boolean(password) && password.length >= 8 && /[0-9]/.test(password);

// A dedicated, throwaway client instead of the shared app-wide one. This page
// sets a session directly from a one-time recovery link's tokens - if the
// browser tab loses and regains focus while the person is typing their new
// password (a completely normal thing to do), the shared client's background
// auto-refresh timer fires and races that freshly-set session, using the
// already-rotated refresh token and wiping it out ("Auth session missing!"
// right when they submit, even though the page just showed their email a
// moment earlier). Confirmed live: an isolated Node script reproducing the
// exact same setSession -> wait -> updateUser sequence works perfectly with
// autoRefreshToken off, so this is scoped to just this instance rather than
// disabling auto-refresh for the whole app (other pages need it for normal
// day-long admin sessions).
//
// storageKey must be unique too - this module stays resident for the whole
// SPA session (it's a module-level singleton, not tied to component mount),
// so if it shares the default storage key with the main app client, the two
// GoTrueClient instances cross-talk over that shared key ("Multiple
// GoTrueClient instances detected... may produce undefined behavior") - this
// signOut() call below was bleeding into a completely separate, later,
// normal login on /admin/login and killing that session right as
// ProtectedRoute checked it, even though 2FA had just succeeded.
const authActionClient = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  { auth: { detectSessionInUrl: false, autoRefreshToken: false, persistSession: false, storageKey: 'sb-admin-auth-action-throwaway' } }
);

// Landing page for admin password-setup/reset links (backend/routes/admins.js
// generateLink({type:'recovery'}) on admin creation or "Reset password").
// Same pattern as my-app's AuthAction.jsx: Supabase's link already completes
// verification server-side and redirects here with a session in the URL hash
// - this just waits for the PASSWORD_RECOVERY event and lets them set a new
// password, admin-only currently since that's the only action type admins.js
// ever sends a link for.
export default function AdminAuthAction() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready | success | error
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const sessionSetupStarted = useRef(false);

  useEffect(() => {
    // Set the session directly from the URL hash's tokens (detectSessionInUrl
    // is off on authActionClient, so this is the only thing that processes
    // the hash). React 18 StrictMode still double-invokes this effect in dev
    // though, and refresh tokens are single-use - a second setSession() call
    // with the same (now-rotated) refresh token fails and wipes out the
    // session the first call had just established, so this must run at most
    // once regardless of how many times the effect fires.
    if (sessionSetupStarted.current) return;
    sessionSetupStarted.current = true;

    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');

    if (!accessToken || !refreshToken) {
      console.error('no tokens in hash:', window.location.hash);
      setStatus('error');
      setError('Denne lenken er ugyldig eller har utløpt. Be om en ny lenke.');
      return;
    }

    authActionClient.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }).then(({ data, error: sessionError }) => {
      if (sessionError || !data.session) {
        console.error('setSession failed:', sessionError);
        setStatus('error');
        setError('Denne lenken er ugyldig eller har utløpt. Be om en ny lenke.');
        return;
      }
      setEmail(data.session.user.email);
      setStatus('ready');
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isPasswordStrongEnough(password)) {
      setError('Passordet må være minst 8 tegn og inneholde minst ett tall.');
      return;
    }
    setSubmitting(true);
    try {
      const { error: updateError } = await authActionClient.auth.updateUser({ password });
      if (updateError) throw updateError;
      await authActionClient.auth.signOut();

      // Sign in on the SHARED client with the password just set, so the next
      // page lands straight on the OTP screen instead of making them retype
      // an email+password they only just proved they know. Best-effort - if
      // this fails for any reason, "admin_auto_continue" just never gets set
      // and they fall back to the normal login form, no worse than before.
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (!signInError) {
        sessionStorage.setItem('admin_auto_continue', 'true');
      }

      setStatus('success');
    } catch (err) {
      console.error('updateUser failed:', err);
      setError('Kunne ikke sette passordet. Lenken kan ha utløpt - be om en ny.');
    }
    setSubmitting(false);
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <div className="admin-login-header">
          <h1>PlayWorld Admin</h1>
          {status === 'loading' && <p>Sjekker lenke...</p>}
          {status === 'ready' && <p>Sett passord for {email}</p>}
          {status === 'success' && <p>Passord satt!</p>}
          {status === 'error' && <p>Noe gikk galt</p>}
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {status === 'ready' && (
          <form onSubmit={handleSubmit} className="admin-login-form">
            <div className="form-group">
              <label htmlFor="password">Nytt passord</label>
              <input
                type="password"
                id="password"
                placeholder="Minst 8 tegn, minst ett tall"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                autoFocus
              />
            </div>
            <button type="submit" className="login-button" disabled={submitting}>
              {submitting ? 'Lagrer...' : 'Lagre passord'}
            </button>
          </form>
        )}

        {(status === 'success' || status === 'error') && (
          <button type="button" className="login-button" onClick={() => navigate('/admin/login')}>
            {status === 'error' ? 'Tilbake til innlogging' : 'Fortsett til innlogging'}
          </button>
        )}

        <div className="admin-login-footer">
          <p>Kun for autorisert personell</p>
        </div>
      </div>
    </div>
  );
}
